import ImageUpload from "@/components/ImageUpload";
import FacebookLinkField from "@/components/FacebookLinkField";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const GENDERS = [["male", "Male"], ["female", "Female"], ["mixed", "Non-binary / Other"], ["prefer_not", "Prefer not to say"]];
const HANDS = [["right", "Right"], ["left", "Left"], ["ambidextrous", "Ambidextrous"]];
const SKILLS = ["2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0+"];
const PREFS = [["singles", "Singles"], ["doubles", "Doubles"], ["mixed", "Mixed Doubles"], ["recreational", "Recreational"], ["competitive", "Competitive"]];

function Label({ children, required }) {
  return <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>{children}{required && <span className="text-primary-c"> *</span>}</label>;
}

// Player profile inputs (excludes name/email/password which the auth form owns).
export default function PlayerProfileFields({ value, onChange, publicUpload = false, hidePhone = false }) {
  const p = value || {};
  const set = (patch) => onChange({ ...p, ...patch });
  const loc = p.location || {};
  const ec = p.emergency_contact || {};
  const prefs = p.playing_preferences || [];
  const togglePref = (v) => set({ playing_preferences: prefs.includes(v) ? prefs.filter((x) => x !== v) : [...prefs, v] });

  return (
    <div className="space-y-4" data-testid="player-profile-fields">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!hidePhone && <div><Label required>Phone</Label><input data-testid="pp-phone" value={p.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="Mobile number" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>}
        <div><Label required>Date of birth</Label><input data-testid="pp-dob" type="date" value={p.date_of_birth || ""} onChange={(e) => set({ date_of_birth: e.target.value })} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label required>Gender</Label>
          <select data-testid="pp-gender" value={p.gender || ""} onChange={(e) => set({ gender: e.target.value })} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }}>
            <option value="">Select…</option>
            {GENDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label>Dominant hand</Label>
          <select data-testid="pp-hand" value={p.dominant_hand || ""} onChange={(e) => set({ dominant_hand: e.target.value })} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }}>
            <option value="">Select…</option>
            {HANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label required>City</Label><input data-testid="pp-city" value={loc.city || ""} onChange={(e) => set({ location: { ...loc, city: e.target.value } })} placeholder="City" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
        <div><Label required>Zip / Postal code</Label><input data-testid="pp-zip" value={loc.zip || ""} onChange={(e) => set({ location: { ...loc, zip: e.target.value } })} placeholder="Zip code" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Self-rated skill</Label>
          <select data-testid="pp-skill" value={p.self_rated_skill || ""} onChange={(e) => set({ self_rated_skill: e.target.value })} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }}>
            <option value="">Select…</option>
            {SKILLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><Label>DUPR ID / rating link</Label><input data-testid="pp-dupr" value={p.dupr_id || ""} onChange={(e) => set({ dupr_id: e.target.value })} placeholder="DUPR ID or profile link" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
      </div>

      <div><Label>Club</Label><input data-testid="pp-club" value={p.club || ""} onChange={(e) => set({ club: e.target.value })} placeholder="Home club (optional)" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>

      <FacebookLinkField value={p.facebook_url || ""} onChange={(v) => set({ facebook_url: v })} />

      <div>
        <Label>Playing preferences</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {PREFS.map(([v, l]) => (
            <button type="button" key={v} data-testid={`pp-pref-${v}`} onClick={() => togglePref(v)}
              className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors"
              style={prefs.includes(v) ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label required>Emergency contact name</Label><input data-testid="pp-ec-name" value={ec.name || ""} onChange={(e) => set({ emergency_contact: { ...ec, name: e.target.value } })} placeholder="Contact name" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
        <div><Label required>Emergency contact phone</Label><input data-testid="pp-ec-phone" value={ec.phone || ""} onChange={(e) => set({ emergency_contact: { ...ec, phone: e.target.value } })} placeholder="Contact phone" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
      </div>

      <div>
        <Label required>Profile photo</Label>
        <div className="mt-1.5 max-w-[220px]">
          <ImageUpload testid="pp-photo" label="Upload headshot" value={p.profile_photo || null} onChange={(url) => set({ profile_photo: url })} aspect="aspect-square" hint="Clear headshot" publicUpload={publicUpload} />
        </div>
      </div>
    </div>
  );
}

export function validatePlayerProfile(p) {
  const miss = [];
  if (!p?.phone) miss.push("phone");
  if (!p?.date_of_birth) miss.push("date of birth");
  if (!p?.gender) miss.push("gender");
  if (!p?.location?.city) miss.push("city");
  if (!p?.location?.zip) miss.push("zip");
  if (!p?.emergency_contact?.name) miss.push("emergency contact name");
  if (!p?.emergency_contact?.phone) miss.push("emergency contact phone");
  if (!p?.profile_photo) miss.push("profile photo");
  return miss;
}
